import { useEffect, useState } from 'react'
import { Icon } from '../components/Icon'
import { useUI } from '../store/ui'
import { listRoles, listPermissions, createRole, updateRole, deleteRole, type Role, type Permission } from '../api/admin'
import { roleLabel } from '../lib/format'

const COLS = 'minmax(200px,1fr) 2fr 90px 110px'

export function Roles() {
  const showToast = useUI((s) => s.showToast)
  const [roles, setRoles] = useState<Role[] | null>(null)
  const [perms, setPerms] = useState<Permission[]>([])
  const [editing, setEditing] = useState<Role | 'new' | null>(null)
  const [err, setErr] = useState('')

  async function load() {
    setErr('')
    try {
      const [r, p] = await Promise.all([listRoles(), listPermissions()])
      setRoles(r)
      setPerms(p)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load roles')
    }
  }
  useEffect(() => {
    void load()
  }, [])

  async function remove(role: Role) {
    if (!confirm(`Delete role "${role.name}"? Users keep no membership to it.`)) return
    try {
      await deleteRole(role.id)
      showToast('Role deleted', 'success')
      void load()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Delete failed', 'danger')
    }
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Roles &amp; permissions</h1>
          <div className="desc">Roles bundle permissions. Built-in roles can’t be renamed or deleted, but their permission sets stay editable.</div>
        </div>
        <button className="btn primary" onClick={() => setEditing('new')}>
          <Icon name="plus" size={16} /> New role
        </button>
      </div>

      {err && <div className="auth-error mt8">{err}</div>}

      <div className="tbl tbl-scroll">
        <div className="tr head" style={{ gridTemplateColumns: COLS }}>
          <div>Role</div>
          <div>Permissions</div>
          <div className="cell-c">Members</div>
          <div className="cell-r">Manage</div>
        </div>
        {roles === null &&
          [0, 1, 2].map((i) => (
            <div className="tr body" style={{ gridTemplateColumns: COLS }} key={i}>
              <div className="skeleton" style={{ width: '60%' }} />
              <div className="skeleton" style={{ width: '90%' }} />
              <div className="skeleton" style={{ width: 30 }} />
              <div />
            </div>
          ))}
        {roles?.map((r) => (
          <div className="tr body click" style={{ gridTemplateColumns: COLS }} key={r.id} onClick={() => setEditing(r)}>
            <div>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                {r.system ? roleLabel(r.name) : r.name}
                {r.system && (
                  <span className="chip" style={{ color: 'var(--muted)', borderColor: 'transparent', background: 'var(--surface-3)' }}>
                    built-in
                  </span>
                )}
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>{r.description}</div>
            </div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.permissions.join('  ·  ') || '—'}
            </div>
            <div className="cell-c num">{r.member_count}</div>
            <div className="row-actions" onClick={(e) => e.stopPropagation()}>
              <button className="btn ghost sm" title="Edit" onClick={() => setEditing(r)}>
                <Icon name="edit" size={15} />
              </button>
              <button className="btn ghost sm" title="Delete" disabled={r.system} onClick={() => remove(r)}>
                <Icon name="trash" size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <RoleEditor
          role={editing === 'new' ? null : editing}
          allPerms={perms}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            showToast('Role saved', 'success')
            void load()
          }}
        />
      )}
    </div>
  )
}

function RoleEditor({
  role,
  allPerms,
  onClose,
  onSaved,
}: {
  role: Role | null
  allPerms: Permission[]
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.permissions ?? []))
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const isSystem = role?.system ?? false

  function toggle(name: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  async function save() {
    setErr('')
    setBusy(true)
    try {
      const perms = Array.from(selected)
      if (role) {
        await updateRole(role.id, { description, permissions: perms, ...(isSystem ? {} : { name }) })
      } else {
        await createRole({ name, description, permissions: perms })
      }
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="drawer" role="dialog" aria-label="Edit role" style={{ width: 'min(600px,96vw)' }}>
        <div className="drawer-head">
          <h3>{role ? (isSystem ? roleLabel(role.name) : `Edit ${role.name}`) : 'New role'}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="drawer-body">
          <div className="field">
            <label>Name</label>
            <input className="input" value={name} disabled={isSystem} onChange={(e) => setName(e.target.value)} />
            {isSystem && <div className="auth-hint">Built-in role — name is fixed.</div>}
          </div>
          <div className="field">
            <label>Description</label>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="field">
            <label>Permissions ({selected.size})</label>
            <div className="checkgrid">
              {allPerms.map((p) => (
                <label key={p.id} className={'checkrow' + (selected.has(p.name) ? ' on' : '')}>
                  <input type="checkbox" checked={selected.has(p.name)} onChange={() => toggle(p.name)} />
                  <span>
                    <span className="mono" style={{ fontWeight: 600 }}>{p.name}</span>
                    <br />
                    <span className="muted" style={{ fontSize: 11 }}>{p.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          {err && <div className="auth-error">{err}</div>}
        </div>
        <div className="drawer-foot">
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !name} onClick={save}>
            {busy ? 'Saving…' : 'Save role'}
          </button>
        </div>
      </div>
    </>
  )
}
